import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight } from "lucide-react";
import { useBlogArticle, fetchRelated, fetchRelatedServices } from "@/lib/blogApi";
import { signalPrerenderReady } from "@/lib/prerenderReady";
import DynamicRouteLoader from "@/components/site/DynamicRouteLoader";
import LexicalRenderer from "@/components/blog/LexicalRenderer";
import { ArticleCta, ArticleNewsletter, ArticleTrust } from "@/components/blog/ArticleBlocks";
import TableOfContents from "@/components/blog/TableOfContents";
import PrevNextNav from "@/components/blog/PrevNextNav";
import PeerCountryHubs from "@/components/blog/PeerCountryHubs";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/site/Breadcrumbs";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";
import { trackEvent } from "@/lib/ga";

export default function BlogArticle() {
  const { slug } = useParams();
  const { loading, error, notFound, data: article } = useBlogArticle(slug);
  const { data: seoMeta } = useSeoMetadata();
  const [related, setRelated] = useState([]);
  const [relatedServices, setRelatedServices] = useState([]);

  useEffect(() => {
    if (article?.id) {
      fetchRelated(article.id).then(setRelated).catch(() => setRelated([]));
      fetchRelatedServices(article.id).then(setRelatedServices).catch(() => setRelatedServices([]));
    }
  }, [article?.id]);

  // Prerender.io-compatible readiness signal.
  // "ok"       — success (article rendered).  Cache freely.
  // "notFound" — API 404. Article genuinely does not exist. Cache is safe
  //              because the noindex,nofollow meta is emitted below.
  // "error"    — transient failure (network / 5xx / timeout). We DO NOT
  //              flip the flag so crawler-cache does not snapshot the
  //              "Content temporarily unavailable" fallback UI. The
  //              crawler will retry the URL on its next pass.
  // See /app/docs/SEO_CONTENT_BIBLE.md §Prerender rules.
  useEffect(() => {
    if (loading) return;
    if (notFound) { signalPrerenderReady("notFound"); return; }
    if (error || !article) { signalPrerenderReady("error"); return; }
    signalPrerenderReady("ok");
  }, [loading, error, notFound, article]);

  // Loading state: MUST NOT mount a page-shell (<main>Loading…</main>)
  // as the first child of #root. Emergent's crawler-renderer would
  // capture that shell as a valid snapshot without title/desc/canonical.
  // We render the indicator via a portal into document.body (outside
  // #root) so #root stays effectively empty until data is ready.
  // See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (loading) {
    // Bing "Add a title tag" fix: renderer may snapshot before data
    // loads. Emit a slug-derived, non-empty <title> + canonical so the
    // captured HTML is never title-less. Real SEO metadata (from
    // useSeoMetadata) still overrides this once the article resolves.
    const _loadingTitle = _slugToTitle(slug);
    return (
      <>
        <Helmet>
          <title>{`${_loadingTitle} — Manufacturing and Trading Services`}</title>
          <link rel="canonical" href={_absUrl(`/blog/${slug}`)} />
        </Helmet>
        <DynamicRouteLoader variant="loading" testId="article-loading" />
      </>
    );
  }
  // Confirmed 404 from the API — article genuinely does not exist. Safe
  // to emit noindex,nofollow (Google-recommended SPA soft-404 signal).
  if (notFound) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16" data-testid="article-not-found">
        <Helmet>
          <title>Article not found — Manufacturing and Trading Services</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <h1 className="text-2xl font-semibold text-neutral-900 mb-4">Article not found</h1>
        <Link to="/blog" className="text-neutral-600 hover:text-neutral-900">← Back to Blog</Link>
      </main>
    );
  }
  // Transient error (network, 5xx, timeout) after retry. The article may
  // exist — we just failed to load it. DO NOT emit noindex here: a
  // crawler / prerender that snapshots this state must not cache a
  // permanent "hide from index" signal for a URL that is otherwise valid.
  // Google will re-crawl this URL later; noindex would remove it from
  // the index for hours after the actual data is back.
  // Transient error: same guarantee — no <main> shell in #root. The
  // portal keeps the `data-testid="article-unavailable"` marker in the
  // document so the smart-poll in index.html still refuses to flip
  // prerenderReady. signalPrerenderReady("error") above also stays.
  // See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (error || !article) {
    return <DynamicRouteLoader variant="unavailable" testId="article-unavailable" />;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const seo = article.seo || {};
  const adv = article.seoAdvanced || {};
  const canonical = _absUrl(seo.canonicalPath || `/blog/${article.slug}`);
  const metaTitle = seo.metaTitle || article.title;
  const metaDescription = seo.metaDescription || article.excerpt || "";
  const ogImage = _absUrl(seo.ogImage?.url || article.featuredImage?.url) || seoMeta?.logoUrl;
  const twitterImage = _absUrl(seo.twitterImage?.url || seo.ogImage?.url || article.featuredImage?.url) || seoMeta?.logoUrl;

  // Breadcrumb items — Home → Blog → Vertical. H1 provides the article
  // title so the breadcrumb is kept compact (no repeat of the title).
  const vertical = article.vertical || {};
  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Blog", href: "/blog" },
    ...(vertical.slug ? [{ name: vertical.name || vertical.slug, href: `/verticals/${vertical.slug}` }] : []),
  ];

  // BreadcrumbList JSON-LD includes the article as the terminal item —
  // Google's spec expects the full trail. The VISIBLE breadcrumb is
  // trimmed above, but the structured data below stays complete.
  const breadcrumbItemsForSchema = [...breadcrumbItems, { name: article.title }];

  // JSON-LD: prefer CMS-provided (adv.*) then auto-generate fallbacks
  // from article data so every article always ships full structured data.
  const blogPostingSchema = adv.articleSchema || buildBlogPostingSchema({
    article, canonical, ogImage, seoMeta, description: metaDescription,
  });
  const breadcrumbSchema = adv.breadcrumbSchema || buildBreadcrumbSchema(breadcrumbItemsForSchema, origin);
  const faqSchema = adv.faqSchema || buildFaqSchema(article.content);

  const jsonLd = [blogPostingSchema, breadcrumbSchema, faqSchema].filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={seo.robots || "index,follow"} />
        {/* Open Graph */}
        <meta property="og:title" content={seo.ogTitle || metaTitle} />
        {(seo.ogDescription || metaDescription) && <meta property="og:description" content={seo.ogDescription || metaDescription} />}
        <meta property="og:type" content={seo.ogType || "article"} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={seo.ogLocale || "en_US"} />
        <meta property="og:site_name" content={seo.ogSiteName || seoMeta?.displayName || "Manufacturing & Trading Services"} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {article.publishedAt && <meta property="article:published_time" content={article.publishedAt} />}
        {article.updatedAt && <meta property="article:modified_time" content={article.updatedAt} />}
        {(vertical.name || vertical.slug) && (
          <meta property="article:section" content={vertical.name || vertical.slug} />
        )}
        {/* Twitter Card */}
        <meta name="twitter:card" content={seo.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={seo.twitterTitle || seo.ogTitle || metaTitle} />
        {(seo.twitterDescription || seo.ogDescription || metaDescription) && (
          <meta name="twitter:description" content={seo.twitterDescription || seo.ogDescription || metaDescription} />
        )}
        {twitterImage && <meta name="twitter:image" content={twitterImage} />}
        {/* JSON-LD (BlogPosting + BreadcrumbList + optional FAQPage;
            Organization + WebSite are added automatically by <Seo>). */}
        {jsonLd.map((schema, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 py-12">
        <Breadcrumbs items={breadcrumbItems} className="mb-6" />

        {/* Part-of-pillar-guide strap — visible only when the CMS
            operator has linked this article to a Pillar Guide via
            `linkedGuide`. Reinforces topical clustering and gives
            readers a direct path back to the pillar page. */}
        {article.linkedGuide && typeof article.linkedGuide === "object" && article.linkedGuide.slug && (
          <Link
            to={`/guides/${article.linkedGuide.slug}`}
            data-testid="article-linked-guide-strap"
            className="group mb-6 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-1.5 text-[12px] text-neutral-600 hover:border-neutral-900 hover:text-neutral-900 transition-colors"
          >
            <span className="uppercase tracking-[0.15em] text-[10px] text-neutral-400 group-hover:text-neutral-500">
              Part of
            </span>
            <span className="font-medium">
              {article.linkedGuide.displayTitle || article.linkedGuide.title}
            </span>
            <ArrowUpRight size={13} className="opacity-70 group-hover:opacity-100" />
          </Link>
        )}

        <header className="mb-8">
          {/* Visible H1 uses `displayTitle` when the operator has set one
              in CMS — decouples on-page presentation from the SEO title
              (which continues to drive <title>, OG, Twitter, JSON-LD).
              Falls back to `title` when displayTitle is empty. */}
          <h1
            className="article-h1"
            data-testid="article-h1"
          >
            {article.displayTitle || article.title}
          </h1>
          {/* Reading time — only unobtrusive on-page metadata at the top.
              Author/published/updated dates live at the article FOOTER
              (per final QA spec) so the opening stays focused on the
              headline + featured image. All structured-data dates in
              JSON-LD are unchanged. */}
          {article.readingTime && (
            <p className="mt-2 text-xs text-neutral-400" data-testid="article-reading-time">
              {article.readingTime} min read
            </p>
          )}
        </header>

        {article.featuredImage?.url && (
          <div className="aspect-video overflow-hidden rounded-lg bg-neutral-100 mb-10">
            <img src={article.featuredImage.url}
                 alt={article.featuredImage.alt || article.title}
                 width={article.featuredImage.width || 1200}
                 height={article.featuredImage.height || 675}
                 loading="eager"
                 className="w-full h-full object-cover" />
          </div>
        )}

        {/* Split the article body into an INTRO (everything before the
            first H2) and a REST. TOC is rendered between them so readers
            get a proper opening before the navigation, matching how
            established B2B publications structure long-form articles. */}
        <ArticleBody
          content={article.content}
          excerpt={article.excerpt}
        />

        {/* Article metadata footer — Author + Last updated. Moved from
            top-of-article per final QA spec so the opening stays focused
            on the headline + hero. All schema.org / OG / Twitter dates
            in <head> remain unchanged. */}
        <ArticleMetaFooter article={article} publisherName={seoMeta?.displayName} />

        <ArticleCta article={article} />
        <ArticleTrust />
        {article?.contentType === "country-hub" && (
          <PeerCountryHubs currentCode={article?.countryHubFor} />
        )}
        {relatedServices.length > 0 && <RelatedServicesBlock services={relatedServices} articleSlug={article.slug} />}
        {related.length > 0 && <RelatedArticlesBlock articles={related} />}
        <PrevNextNav slug={article.slug} />
        <ArticleNewsletter article={article} />
      </article>
    </>
  );
}

function RelatedServicesBlock({ services, articleSlug }) {
  return (
    <section className="my-14 border-t border-neutral-200 pt-10" data-testid="related-services">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">Related services</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {services.map((s) => (
          <a key={s.id || s.slug} href={s.href}
             onClick={() => trackEvent("related_service_click", {
               service_slug: s.slug,
               article_slug: articleSlug || "",
             })}
             className="block border border-neutral-200 rounded p-4 hover:border-neutral-900 transition-colors"
             data-testid={`related-service-${s.slug}`}>
            <div className="font-semibold text-neutral-900 mb-1 line-clamp-2">{s.title}</div>
            {s.summary && <div className="text-sm text-neutral-600 line-clamp-3">{s.summary}</div>}
          </a>
        ))}
      </div>
    </section>
  );
}

function RelatedArticlesBlock({ articles }) {
  return (
    <section className="my-14 border-t border-neutral-200 pt-10" data-testid="related-articles">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">Related articles</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {articles.map((a) => (
          <Link key={a.id} to={`/blog/${a.slug}`} className="block border border-neutral-200 rounded p-4 hover:border-neutral-900 transition-colors">
            <div className="font-semibold text-neutral-900 mb-1 line-clamp-2">{a.title}</div>
            {a.excerpt && <div className="text-sm text-neutral-600 line-clamp-2">{a.excerpt}</div>}
          </Link>
        ))}
      </div>
    </section>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function _absUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    return new URL(pathOrUrl, window.location.origin).toString();
  } catch { return pathOrUrl; }
}

// Derives a human-readable, non-empty title from a route slug for the
// loading-state <Helmet>. Strips a trailing short hex hash (e.g. the
// "-f4f834b1" suffix Payload adds to unique blog slugs) and Title-Cases
// the remaining words. Used only until real SEO metadata loads.
function _slugToTitle(slug) {
  if (!slug) return "Article";
  return String(slug)
    .replace(/-[0-9a-f]{6,10}$/i, "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || "Article";
}

function _fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US",
      { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

function _bodyStartsWithHeading(content) {
  const kids = content?.root?.children;
  if (!Array.isArray(kids) || kids.length === 0) return false;
  const first = kids[0]?.type === 'heading' ? kids[1] : kids[0];
  return first?.type === 'heading';
}

/**
 * Splits the article Lexical tree into an INTRO block (everything before
 * the first H2, capped at 3 nodes so we never overwhelm the reader with
 * a wall of text before the TOC) and the REST. Renders:
 *
 *   INTRO paragraphs (or excerpt fallback when body opens with a heading)
 *   TableOfContents  (collapsible on mobile via <details>)
 *   REST of the article
 *
 * The split is purely visual — the underlying content is untouched, no
 * SEO field is modified, no JSON-LD changes. Order of headings inside
 * the article body is preserved so all H2/H3 anchors keep working.
 */
function ArticleBody({ content, excerpt }) {
  const kids = content?.root?.children;
  if (!Array.isArray(kids) || kids.length === 0) {
    return (
      <div data-testid="article-content">
        <LexicalRenderer content={content} />
      </div>
    );
  }

  // Find where the first H2 starts. Cap the intro at 3 leading nodes so
  // very long lead sections don't push the TOC too far down.
  let firstH2Idx = kids.findIndex(
    (n) => n?.type === "heading" && /^h2$/i.test(n.tag || "")
  );
  if (firstH2Idx < 0) firstH2Idx = kids.length;
  const introEnd = Math.min(firstH2Idx, 3);

  const introKids = kids.slice(0, introEnd);
  const restKids = kids.slice(introEnd);

  const introContent = { ...content, root: { ...content.root, children: introKids } };
  const restContent  = { ...content, root: { ...content.root, children: restKids } };

  // Excerpt fallback when body opens straight into a heading — same
  // guarantee as before: never open with a section title.
  const bodyStartsWithHeading = introKids.length === 0
    || (introKids[0]?.type === "heading");

  return (
    <>
      {excerpt && bodyStartsWithHeading && (
        <p
          className="blog-lead text-lg md:text-xl text-neutral-700 leading-relaxed mb-8"
          data-testid="article-lead"
        >
          {excerpt}
        </p>
      )}
      {introKids.length > 0 && (
        <div data-testid="article-intro">
          <LexicalRenderer content={introContent} />
        </div>
      )}
      <TocCollapsible content={content} />
      {restKids.length > 0 && (
        <div data-testid="article-content">
          <LexicalRenderer content={restContent} />
        </div>
      )}
    </>
  );
}

/**
 * TOC that is collapsed by default on mobile (< 640 px) and open on
 * desktop. Uses a CSS media-query to control visibility so there is no
 * SSR/hydration flash — the React state only reflects the USER's
 * subsequent toggle intent. Never depends on window.matchMedia at
 * initial render, which avoided a subtle timing bug where mobile
 * viewport hydrated with the panel visible.
 */
function TocCollapsible({ content }) {
  // `null` = no explicit override; CSS handles the default (open on
  // desktop, collapsed on mobile). Once the user clicks the toggle
  // (mobile only), we take control with a boolean.
  const [userOpen, setUserOpen] = React.useState(null);
  const attrValue = userOpen === null ? "auto" : (userOpen ? "true" : "false");

  return (
    <aside
      className="article-toc"
      data-toc-open={attrValue}
      data-testid="toc-container"
    >
      <button
        type="button"
        onClick={() => setUserOpen((v) => v === true ? false : true)}
        className="article-toc-toggle"
        aria-controls="toc-panel"
        data-testid="toc-toggle"
      >
        <span className="article-toc-label">In this article</span>
        <span
          className="article-toc-chevron sm:hidden"
          aria-hidden="true"
        >
          +
        </span>
      </button>
      <div id="toc-panel" className="article-toc-panel">
        <TableOfContents content={content} />
      </div>
    </aside>
  );
}

/**
 * Author + Last updated block rendered at the FOOTER of the article
 * body, before the CTA / Related Articles. Uses the CMS-driven
 * organisation display name so a rebrand doesn't require code changes.
 * All schema.org datePublished / dateModified in JSON-LD are set by
 * the article's <Helmet> block — untouched by this component.
 */
function ArticleMetaFooter({ article, publisherName }) {
  const publisher = publisherName || "Manufacturing & Trading Services";
  const updatedAt = article.updatedAt || article.publishedAt;
  if (!article.publishedAt && !updatedAt) return null;
  return (
    <section
      className="article-meta-footer"
      aria-label="Article information"
      data-testid="article-meta-footer"
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <dt className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">Author</dt>
          <dd className="mt-1 text-sm text-neutral-800" data-testid="article-byline">
            {publisher}
          </dd>
        </div>
        {updatedAt && (
          <div>
            <dt className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">Last updated</dt>
            <dd className="mt-1 text-sm text-neutral-800">
              <time dateTime={updatedAt} data-testid="article-updated-at">
                {_fmtDate(updatedAt)}
              </time>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ── JSON-LD auto-generation ──────────────────────────────────────────────

/** BlogPosting schema, using Organization as both author and publisher.
 * Every field required by Google's Article/BlogPosting spec is populated. */
function buildBlogPostingSchema({ article, canonical, ogImage, seoMeta, description }) {
  const orgId = seoMeta?.baseURL ? `${seoMeta.baseURL}/#organization` : undefined;
  const websiteId = seoMeta?.baseURL ? `${seoMeta.baseURL}/#website` : undefined;
  const orgRef = orgId ? { "@id": orgId } : { "@type": "Organization", name: seoMeta?.displayName || "Manufacturing and Trading Services" };
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": canonical ? `${canonical}#article` : undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: article.title,
    description: description || undefined,
    image: ogImage ? [ogImage] : undefined,
    author: orgRef,
    publisher: orgRef,
    isPartOf: websiteId ? { "@id": websiteId } : undefined,
    datePublished: article.publishedAt || article.createdAt || undefined,
    dateModified: article.updatedAt || article.publishedAt || undefined,
    inLanguage: "en",
    url: canonical || undefined,
    articleSection: article.vertical?.name || article.vertical?.slug || undefined,
    keywords: [article.seo?.metaKeywords, article.primaryKeyword]
      .filter(Boolean).flat().join(", ") || undefined,
  };
  return _prune(schema);
}

/** FAQPage schema — extract Q/A pairs from the Lexical content.
 * Detects two patterns:
 *   1. Payload block nodes with blockType === 'faqBlock' / 'faq' containing
 *      an `items` array of { question, answer } objects.
 *   2. Consecutive H3 → paragraph pairs inside a section whose H2 contains
 *      "FAQ", "Frequently Asked" or ends with a question mark.
 * Returns null when < 2 valid Q/A pairs are found. */
function buildFaqSchema(content) {
  if (!content || typeof content !== "object") return null;
  const root = content.root || content;
  const pairs = [];

  // Pattern 1: dedicated FAQ blocks
  const collectBlocks = (node) => {
    if (!node || typeof node !== "object") return;
    const bt = node.blockType || node.fields?.blockType || node.type;
    if (typeof bt === "string" && /faq/i.test(bt)) {
      const items = node.items || node.fields?.items || node.fields?.questions || [];
      for (const it of (Array.isArray(items) ? items : [])) {
        const q = (it.question || it.q || "").trim();
        const a = (it.answer || it.a || "").trim();
        if (q && a) pairs.push({ question: q, answer: a });
      }
    }
    for (const c of (node.children || [])) collectBlocks(c);
  };
  collectBlocks(root);

  // Pattern 2: H3 → paragraph pairs under a FAQ-ish H2
  const kids = Array.isArray(root.children) ? root.children : [];
  let inFaqSection = false;
  for (let i = 0; i < kids.length; i++) {
    const n = kids[i];
    if (n?.type === "heading" && (n.tag === "h2" || n.tag === "H2")) {
      const t = _flatText(n).toLowerCase();
      inFaqSection = /faq|frequently asked/i.test(t);
      continue;
    }
    if (inFaqSection && n?.type === "heading" && /h3/i.test(n.tag || "")) {
      const q = _flatText(n).trim();
      // Collect following paragraphs until next heading
      const answerBits = [];
      for (let j = i + 1; j < kids.length; j++) {
        const m = kids[j];
        if (m?.type === "heading") break;
        if (m?.type === "paragraph") answerBits.push(_flatText(m));
      }
      const a = answerBits.join(" ").trim();
      if (q && a) pairs.push({ question: q, answer: a });
    }
  }

  if (pairs.length < 2) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pairs.map((p) => ({
      "@type": "Question",
      name: p.question,
      acceptedAnswer: { "@type": "Answer", text: p.answer },
    })),
  };
}

function _flatText(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.type === "text") return node.text || "";
  return (node.children || []).map(_flatText).join("");
}

function _prune(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    out[k] = v;
  }
  return out;
}
